import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Home, LogOut } from 'lucide-react';
import { usePortalText } from '@/portal/hooks/usePortalText';
import { PORTAL_LOGIN_PATH } from '@/portal/config';
import { usePortalSession } from '@/portal/stores/portalSessionStore';
import { navItemsFor } from '@/portal/navItems';
import { roleFromToken } from '@/portal/utils/jwt';
import { clsx } from 'clsx';

const itemClass =
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-light-text dark:text-dark-text outline-none hover:bg-brand-purple-base/10 focus:bg-brand-purple-base/10 dark:hover:bg-white/5 dark:focus:bg-white/5';

/** `overHero`: the header is transparent over the dark hero, so (like the other header items) the text is white in light mode. */
const AccountMenu = ({ overHero = false }: { overHero?: boolean }) => {
  const { t, isRTL } = usePortalText();
  const dir = isRTL ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  const profile = usePortalSession((state) => state.profile);
  const role = roleFromToken(usePortalSession((state) => state.token)) ?? 'customer';
  const endSession = usePortalSession((state) => state.endSession);

  const name = profile?.displayName || profile?.username || '';
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  const handleLogout = () => {
    endSession();
    navigate(PORTAL_LOGIN_PATH, { replace: true });
  };

  return (
    <DropdownMenu.Root dir={dir}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('navAccountMenu')}
          className={clsx(
            'flex items-center gap-2 rounded-full border ps-1 pe-3 py-1 transition-colors dark:border-white/15 dark:text-dark-text dark:hover:bg-white/5',
            overHero
              ? 'border-white/30 text-white hover:bg-white/10'
              : 'border-brand-purple-base/20 text-light-text hover:bg-brand-purple-base/5',
          )}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-orange-base text-sm font-bold text-white">
            {initial}
          </span>
          <span className="hidden max-w-[120px] truncate text-sm font-semibold xl:block">{name}</span>
          <ChevronDown size={16} aria-hidden />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={10}
          className="z-[60] min-w-[220px] rounded-xl border border-brand-purple-base/10 dark:border-white/10 bg-light-primary dark:bg-dark-secondary p-2 shadow-xl"
        >
          <div dir={dir} className="text-start">
            {name && (
              <DropdownMenu.Label className="px-3 pb-2 pt-1 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                {name}
              </DropdownMenu.Label>
            )}

            {navItemsFor(role).map(({ to, labelKey, icon: Icon }) => (
              <DropdownMenu.Item key={to} asChild>
                <Link to={to} className={itemClass}>
                  <Icon size={16} aria-hidden />
                  {t(labelKey)}
                </Link>
              </DropdownMenu.Item>
            ))}

            {role === 'customer' && (
              <DropdownMenu.Item asChild>
                <Link to="/home-visit-request" className={itemClass}>
                  <Home size={16} aria-hidden />
                  {t('navHomeVisit')}
                </Link>
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Separator className="my-2 h-px bg-brand-purple-base/10 dark:bg-white/10" />

            <DropdownMenu.Item
              onSelect={handleLogout}
              className={clsx(itemClass, 'text-red-600 dark:text-red-400')}
            >
              <LogOut size={16} className={clsx(isRTL && 'rotate-180')} aria-hidden />
              {t('navLogout')}
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default AccountMenu;
